import type { Locale } from '@/intl/types'
import { format, isValid, parseISO } from 'date-fns'

export function formatDestination(destinationInformation?: {
  regionName?: string
  countryName?: string
  cityName?: string
}): string {
  if (!destinationInformation)
    return ''

  const { cityName = '', regionName = '', countryName = '' } = destinationInformation
  const segments = [cityName, regionName, countryName]
    .filter(part => part && part.trim())
  return segments.join(', ')
}

export function formatDate(dateString?: string, locale: Locale = 'nl-NL', monthFormat: 'short' | 'long' = 'short'): string {
  const normalizedDateString = dateString?.trim() || ''
  if (!normalizedDateString) {
    return ''
  }

  const date = parseISO(normalizedDateString)
  if (!isValid(date)) {
    return ''
  }
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: monthFormat,
    year: 'numeric',
  })
}

export function formatTime(dateTimeString?: string): string {
  const normalized = dateTimeString?.trim() || ''
  if (!normalized)
    return ''
  const date = parseISO(normalized)
  if (!isValid(date))
    return ''
  return format(date, 'HH:mm')
}

export function formatPeriod(departureDate: string, returnDate: string, locale: Locale = 'nl-NL', monthFormat: 'short' | 'long' = 'short'): string {
  const departure = parseISO(departureDate?.trim() || '')
  const returnD = parseISO(returnDate?.trim() || '')

  if (!isValid(departure) || !isValid(returnD)) {
    return ''
  }

  const sameYear = departure.getFullYear() === returnD.getFullYear()
  const sameMonthAndYear = sameYear && departure.getMonth() === returnD.getMonth()
  const fullReturn = formatDate(returnDate, locale, monthFormat)

  if (sameMonthAndYear) {
    const dayOnly = departure.toLocaleDateString(locale, { day: 'numeric' })
    return `${dayOnly} - ${fullReturn}`
  }

  if (sameYear) {
    const departureWithoutYear = departure.toLocaleDateString(locale, { day: 'numeric', month: monthFormat })
    return `${departureWithoutYear} - ${fullReturn}`
  }

  const formattedDeparture = formatDate(departureDate, locale, monthFormat)
  return `${formattedDeparture} - ${fullReturn}`
}
